import React from "react";
import {
  DataTable,
  DataTableColumnHeader,
  Tag,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@jem-open/jem-ui";
import { ColumnDef } from "@tanstack/react-table";
import EmployeeAuditEntry from "../services/model/EmployeeAuditEntry";
import {
  formatAuditValue,
  formatFieldLabel,
  formatSource,
} from "../utils/employeeAuditLogUtils";

const CHANGE_TYPE_TOOLTIPS: Record<"INITIAL" | "BASELINE", string> = {
  INITIAL: "Values at creation",
  BASELINE: "Reconstructed from system data",
};

const ChangeTypeTag: React.FC<{ label: string; tooltip: string }> = ({
  label,
  tooltip,
}) => (
  <TooltipProvider delayDuration={300}>
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <Tag variant="outline" size="sm">
            {label}
          </Tag>
        </span>
      </TooltipTrigger>
      <TooltipContent variant="dark" side="right">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

type EmployeeAuditLogTableProps = {
  entries: EmployeeAuditEntry[];
};

const auditColumns: ColumnDef<EmployeeAuditEntry>[] = [
  {
    accessorKey: "date_changed",
    size: 200,
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    cell: ({ row }) => (
      <div className="flex items-center gap-xxs">
        <span className="text-sm text-greyscale-text-caption whitespace-nowrap">
          {row.original.date_changed
            ? new Date(row.original.date_changed).toLocaleString()
            : "—"}
        </span>
        {row.original.change_type === "INITIAL" && (
          <ChangeTypeTag
            label="Initial"
            tooltip={CHANGE_TYPE_TOOLTIPS.INITIAL}
          />
        )}
        {row.original.change_type === "BASELINE" && (
          <ChangeTypeTag
            label="Baseline"
            tooltip={CHANGE_TYPE_TOOLTIPS.BASELINE}
          />
        )}
      </div>
    ),
  },
  {
    id: "source",
    accessorFn: (row) => formatSource(row),
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Source" />
    ),
    cell: ({ row }) => (
      <span className="text-sm text-greyscale-text-title">
        {formatSource(row.original)}
      </span>
    ),
  },
  {
    accessorKey: "field",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Field" />
    ),
    cell: ({ row }) => (
      <span className="text-sm">{formatFieldLabel(row.original.field)}</span>
    ),
  },
  {
    accessorKey: "old_value",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Old value" />
    ),
    cell: ({ row }) => (
      <span className="text-sm text-greyscale-text-caption">
        {row.original.old_value != null
          ? formatAuditValue(row.original.old_value)
          : "—"}
      </span>
    ),
  },
  {
    accessorKey: "new_value",
    enableSorting: false,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="New value" />
    ),
    cell: ({ row }) => (
      <span className="text-sm text-greyscale-text-title">
        {row.original.new_value != null
          ? formatAuditValue(row.original.new_value)
          : "—"}
      </span>
    ),
  },
];

const EmployeeAuditLogTable: React.FC<EmployeeAuditLogTableProps> = ({
  entries,
}) => (
  <DataTable
    columns={auditColumns}
    data={entries}
    showPagination={false}
    showToolbar={false}
  />
);

export default EmployeeAuditLogTable;
